import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, Mail, Plus, CalendarClock, Pencil, MapPin, Building2, Ruler, IndianRupee, Tag, User, Clock, CheckCircle2, Circle, FileText, PhoneCall, ArrowRightLeft, StickyNote, Send, FolderKanban, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatINRFull, formatArea, formatDate, formatDateTime, relativeTime, initials, telHref, waHref, mailHref } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { TaskFormDialog } from "@/components/TaskFormDialog";
import { DocumentsPanel } from "@/components/DocumentsPanel";

const ACTIVITY_ICONS = { created: Plus, call: PhoneCall, whatsapp: MessageCircle, email: Mail, note: StickyNote, stage_changed: ArrowRightLeft, task_created: FileText, task_completed: CheckCircle2 };
const ACTIVITY_COLORS = { created: "#0057A8", call: "#059669", whatsapp: "#16A34A", email: "#0284C7", note: "#7C3AED", stage_changed: "#EA580C", task_created: "#0057A8", task_completed: "#059669" };

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { meta, user } = useAuth();
  const canWrite = user?.role !== "ACCOUNTS";
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [note, setNote] = useState("");
  const [converting, setConverting] = useState(false);

  const convertToProject = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      toast.success("Lead converted to project");
      navigate(`/projects/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setConverting(false); }
  };

  const load = useCallback(async () => {
    try {
      const [l, a] = await Promise.all([api.get(`/leads/${id}`), api.get(`/leads/${id}/activities`)]);
      setLead(l.data); setActivities(a.data);
    } catch (e) { if (e.response?.status === 404 || e.response?.status === 403) navigate("/leads"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const logActivity = async (type, description) => {
    await api.post(`/leads/${id}/activities`, { type, description });
    load();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await logActivity("note", note.trim());
    setNote(""); toast.success("Note added");
  };

  const changeStage = async (stage) => {
    await api.patch(`/leads/${id}/stage`, { stage });
    toast.success(`Stage updated to ${stage}`);
    load();
  };

  const toggleTask = async (t) => {
    const status = t.status === "Completed" ? "Pending" : "Completed";
    await api.patch(`/tasks/${t.id}/status`, { status });
    load();
  };

  if (!lead) return (
    <div className="space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="h-40 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>
  );

  const notes = activities.filter((a) => a.type === "note");

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/leads")} className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /> Back to Leads</button>

      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">{lead.company_name}</h1>
              <StatusBadge value={lead.stage} dot />
              <StatusBadge value={lead.priority} type="priority" />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
              <span className="font-mono text-xs">{lead.lead_code}</span>
              {lead.industry && <span className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" />{lead.industry}</span>}
              {lead.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{lead.location}</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Estimated Value</div>
            <div className="font-mono text-2xl font-extrabold text-slate-900">{formatINR(lead.estimated_value)}</div>
            {lead.estimated_area > 0 && <div className="font-mono text-xs text-slate-500">{formatArea(lead.estimated_area)}</div>}
          </div>
        </div>

        {/* Quick actions */}
        <div className="mt-5 flex flex-wrap gap-2">
          {lead.phone && <a href={telHref(lead.phone)} onClick={() => canWrite && logActivity("call", `Called ${lead.contact_person || lead.company_name}`)} data-testid="action-call" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"><Phone className="h-4 w-4 text-emerald-600" /> Call</a>}
          {lead.phone && <a href={waHref(lead.phone)} target="_blank" rel="noreferrer" onClick={() => canWrite && logActivity("whatsapp", `WhatsApp sent to ${lead.contact_person || lead.company_name}`)} data-testid="action-whatsapp" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"><MessageCircle className="h-4 w-4 text-green-600" /> WhatsApp</a>}
          {lead.email && <a href={mailHref(lead.email)} onClick={() => canWrite && logActivity("email", `Emailed ${lead.email}`)} data-testid="action-email" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"><Mail className="h-4 w-4 text-blue-600" /> Email</a>}
          {canWrite && <Button variant="outline" size="sm" onClick={() => setTaskOpen(true)} data-testid="action-add-task" className="h-[38px] gap-1.5"><Plus className="h-4 w-4" /> Add Task</Button>}
          {canWrite && (
            <div className="flex items-center gap-1.5">
              <Select value={lead.stage} onValueChange={changeStage}>
                <SelectTrigger data-testid="action-change-stage" className="h-[38px] w-[150px]"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {canWrite && <Button size="sm" onClick={() => setEditOpen(true)} data-testid="action-edit" className="ml-auto h-[38px] gap-1.5 font-semibold"><Pencil className="h-4 w-4" /> Edit</Button>}
          {lead.project_id ? (
            <Button size="sm" variant="outline" onClick={() => navigate(`/projects/${lead.project_id}`)} data-testid="action-view-project" className={`h-[38px] gap-1.5 border-[#0057A8] font-semibold text-[#0057A8] ${canWrite ? "" : "ml-auto"}`}><FolderKanban className="h-4 w-4" /> View Project</Button>
          ) : lead.stage === "Won" && canWrite ? (
            <Button size="sm" onClick={convertToProject} disabled={converting} data-testid="action-convert-project" className="h-[38px] gap-1.5 font-semibold" style={{ backgroundColor: "#E31E24" }}><FolderKanban className="h-4 w-4" /> {converting ? "Converting…" : "Convert to Project"}</Button>
          ) : null}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="h-auto flex-wrap bg-white border border-slate-200 p-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks {lead.tasks?.length > 0 && <span className="ml-1 rounded-full bg-slate-100 px-1.5 text-[10px]">{lead.tasks.length}</span>}</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="sitevisits" disabled>Site Visits</TabsTrigger>
          <TabsTrigger value="quotations" disabled>Quotations</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <InfoCard title="Contact Information" items={[
                ["Contact Person", lead.contact_person, User],
                ["Phone", lead.phone, Phone],
                ["Email", lead.email, Mail],
                ["Location", lead.location, MapPin],
              ]} />
              <InfoCard title="Project Information" items={[
                ["Industry", lead.industry, Tag],
                ["Flooring System", lead.flooring_system, Building2],
                ["Project Type", lead.project_type, FileText],
                ["Estimated Area", lead.estimated_area ? formatArea(lead.estimated_area) : null, Ruler],
                ["Estimated Value", formatINRFull(lead.estimated_value), IndianRupee],
                ["Expected Date", lead.expected_project_date ? formatDate(lead.expected_project_date) : null, CalendarClock],
              ]} />
              {(lead.total_contracted_area > 0 || lead.quoted_value > 0 || lead.project_name) && (
                <InfoCard title="Commercial & Contract" items={[
                  ["Project Name", lead.project_name, FileText],
                  ["Contracted Area", lead.total_contracted_area ? formatArea(lead.total_contracted_area) : null, Ruler],
                  ["Quoted Rate", lead.quoted_rate ? `₹${lead.quoted_rate} / Sq.Ft (blended)` : null, IndianRupee],
                  ["Quoted Value (taxable)", lead.quoted_value ? formatINRFull(lead.quoted_value) + (lead.quoted_value_overridden ? " · overridden" : "") : null, IndianRupee],
                  ["CGST", lead.cgst_amount ? `${lead.cgst_pct ?? 0}% · ${formatINRFull(lead.cgst_amount)}` : null, IndianRupee],
                  ["SGST", lead.sgst_amount ? `${lead.sgst_pct ?? 0}% · ${formatINRFull(lead.sgst_amount)}` : null, IndianRupee],
                  ["Total incl. GST", lead.grand_total ? formatINRFull(lead.grand_total) : null, IndianRupee],
                  ["Quote Date", lead.quote_date ? formatDate(lead.quote_date) : null, CalendarClock],
                  ["Valid Until", lead.quote_valid_until ? formatDate(lead.quote_valid_until) : null, CalendarClock],
                  ["Expected Start", lead.expected_start_date ? formatDate(lead.expected_start_date) : null, CalendarClock],
                  ["Expected Completion", lead.expected_completion_date ? formatDate(lead.expected_completion_date) : null, CalendarClock],
                ]}>
                  {lead.scope_items && lead.scope_items.length > 0 && (
                    <div className="mt-4 border-t border-slate-100 pt-3" data-testid="lead-scope-items">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Scope of Work</p>
                      <div className="space-y-1.5">
                        {lead.scope_items.map((it, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">{it.system || "—"}</span>
                            <span className="font-mono text-xs text-slate-500">{Number(it.area).toLocaleString("en-IN")} × ₹{it.rate} = <span className="font-semibold text-slate-700">{formatINRFull(it.amount)}</span></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </InfoCard>
              )}
              {lead.notes && (
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <h3 className="mb-2 text-sm font-semibold text-slate-800">Description</h3>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{lead.notes}</p>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="mb-3 text-sm font-semibold text-slate-800">Sales Details</h3>
                <div className="space-y-3">
                  <SideItem label="Assigned To" value={lead.assigned_to_name} avatar />
                  <SideItem label="Lead Source" value={lead.lead_source} />
                  <SideItem label="Next Follow-up" value={lead.next_follow_up ? formatDate(lead.next_follow_up) : "—"} />
                  <SideItem label="Created" value={formatDate(lead.created_at)} />
                  <SideItem label="Last Contacted" value={lead.last_contacted_at ? relativeTime(lead.last_contacted_at) : "Never"} />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
            {activities.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">No activity yet</p> : (
              <ol className="relative space-y-5 border-l border-slate-200 pl-6">
                {activities.map((a) => {
                  const Icon = ACTIVITY_ICONS[a.type] || Circle;
                  const color = ACTIVITY_COLORS[a.type] || "#64748b";
                  return (
                    <li key={a.id} className="relative">
                      <span className="absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white" style={{ backgroundColor: `${color}18`, color }}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="flex flex-wrap items-center justify-between gap-1">
                        <p className="text-sm font-medium text-slate-800">{a.description}</p>
                        <span className="text-xs text-slate-400">{relativeTime(a.created_at)}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">{a.user_name} · {formatDateTime(a.created_at)}</p>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="mt-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Tasks & Reminders</h3>
              <Button size="sm" variant="outline" onClick={() => setTaskOpen(true)} className="gap-1"><Plus className="h-3.5 w-3.5" /> Add</Button>
            </div>
            {(!lead.tasks || lead.tasks.length === 0) ? <p className="py-8 text-center text-sm text-slate-400">No tasks for this lead</p> : (
              <div className="space-y-2">
                {lead.tasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3">
                    <button onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`}>{t.status === "Completed" ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-slate-300" />}</button>
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-medium ${t.status === "Completed" ? "text-slate-400 line-through" : "text-slate-800"}`}>{t.title}</div>
                      <div className="text-xs text-slate-400">Due {formatDate(t.due_date)}</div>
                    </div>
                    <StatusBadge value={t.priority} type="priority" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            {canWrite && (
              <div className="flex gap-2">
                <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note about this lead…" rows={2} className="flex-1" />
                <Button onClick={addNote} data-testid="note-submit" className="gap-1.5 self-end font-semibold"><Send className="h-4 w-4" /> Add</Button>
              </div>
            )}
            <div className="mt-5 space-y-3">
              {notes.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No notes yet</p> :
                notes.map((n) => (
                  <div key={n.id} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                    <p className="whitespace-pre-wrap text-sm text-slate-700">{n.description}</p>
                    <p className="mt-1.5 text-xs text-slate-400">{n.user_name} · {formatDateTime(n.created_at)}</p>
                  </div>
                ))}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <DocumentsPanel entityType="lead" entityId={lead.id} />
        </TabsContent>
      </Tabs>

      <LeadFormDialog open={editOpen} onOpenChange={setEditOpen} lead={lead} onSaved={load} />
      <TaskFormDialog open={taskOpen} onOpenChange={setTaskOpen} defaultLead={lead} onSaved={load} />
    </div>
  );
}

function InfoCard({ title, items, children }) {
  const shown = items.filter(([, v]) => v);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="mb-4 text-sm font-semibold text-slate-800">{title}</h3>
      {shown.length === 0 ? <p className="text-sm text-slate-400">No details provided</p> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {shown.map(([label, value, Icon]) => (
            <div key={label} className="flex items-start gap-2.5">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-400"><Icon className="h-4 w-4" /></div>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
                <div className="truncate text-sm font-medium text-slate-700">{value}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

function SideItem({ label, value, avatar }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
        {avatar && value && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[9px] font-bold text-slate-600">{initials(value)}</span>}
        {value || "—"}
      </span>
    </div>
  );
}
